import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Building2, ImageUp, Trash2 } from "lucide-react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";

const FIELDS = [
  ["razon_social", "Razón social / Nombre de empresa"],
  ["ruc", "RUC"],
  ["direccion", "Dirección"],
  ["telefono", "Teléfono"],
  ["email", "Email"],
  ["web", "Sitio web"],
  ["firmante", "Firmante (nombre en la carta del PDF)"],
  ["cargo", "Cargo del firmante"],
];

export default function Settings() {
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => setForm(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 700 * 1024) { toast.error("El logo debe pesar menos de 700 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => setForm((f) => ({ ...f, logo: reader.result }));
      img.onerror = () => toast.error("La imagen no es válida o está dañada");
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/company", form); toast.success("Datos de empresa guardados"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!form) return <Layout><div className="text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando...</div></Layout>;

  return (
    <Layout>
      <button onClick={() => nav("/")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Volver
      </button>
      <div className="flex items-center gap-2 mb-6">
        <Building2 className="h-6 w-6 text-emerald-600" />
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Datos de empresa</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6 max-w-2xl">Estos datos y el logo aparecerán en las cotizaciones exportadas en PDF y Excel.</p>

      <div className="grid lg:grid-cols-3 gap-6 max-w-4xl">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-display font-bold text-slate-900 text-sm uppercase tracking-wide mb-3">Logo</h3>
          <div className="border border-dashed border-slate-300 rounded-md h-36 flex items-center justify-center overflow-hidden bg-slate-50 mb-3">
            {form.logo
              ? <img src={form.logo} alt="logo" className="max-h-32 max-w-full object-contain" data-testid="logo-preview" />
              : <span className="text-xs text-slate-400">Sin logo</span>}
          </div>
          <label className="flex items-center justify-center gap-2 text-sm border border-slate-300 hover:border-slate-400 rounded-md py-2 cursor-pointer transition-colors">
            <ImageUp className="h-4 w-4" /> Subir logo
            <input data-testid="logo-input" type="file" accept="image/*" className="hidden" onChange={onLogo} />
          </label>
          {form.logo && (
            <button data-testid="logo-remove" onClick={() => setForm((f) => ({ ...f, logo: "" }))}
              className="w-full mt-2 flex items-center justify-center gap-1.5 text-xs text-red-500 hover:text-red-600">
              <Trash2 className="h-3.5 w-3.5" /> Quitar logo
            </button>
          )}
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5">
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map(([k, label]) => (
              <label key={k} className={k === "direccion" ? "sm:col-span-2 block" : "block"}>
                <span className="text-[11px] text-slate-500">{label}</span>
                <input data-testid={`company-${k}`} value={form[k] || ""} onChange={(e) => { const val = e.target.value; setForm((f) => ({ ...f, [k]: val })); }}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" />
              </label>
            ))}
          </div>
          <button data-testid="company-save" onClick={save} disabled={saving}
            className="mt-5 flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2.5 rounded-md transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
          </button>
        </div>
      </div>
    </Layout>
  );
}
